import { useRef } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import type {
  ApprovalLevelApprover,
  ApprovalLevelKind,
} from "../types/ApprovalLevel";
import "./style.css";

export interface AvailableUser {
  id: string;
  name: string;
}

export interface AvailableLob {
  id: string;
  name: string;
}

export interface ApproversSectionProps {
  kind: ApprovalLevelKind;
  approvers: ApprovalLevelApprover[];
  availableUsers: AvailableUser[];
  availableLobs: AvailableLob[];
  onChange: (approvers: ApprovalLevelApprover[]) => void;
}

export const ApproversSection = ({
  kind,
  approvers,
  availableUsers,
  availableLobs,
  onChange,
}: ApproversSectionProps) => {
  const newRowSeq = useRef(0);
  const showLobFilter = kind === "technical";

  const usedUserIds = new Set(
    approvers.map((a) => a.userId).filter((id) => !!id)
  );
  const lobOptions = availableLobs.map((l) => ({ value: l.id, label: l.name }));

  const patchAt = (idx: number, patch: Partial<ApprovalLevelApprover>) =>
    onChange(approvers.map((a, i) => (i === idx ? { ...a, ...patch } : a)));

  const removeAt = (idx: number) =>
    onChange(approvers.filter((_, i) => i !== idx));

  const addRow = () =>
    onChange([
      ...approvers,
      {
        id: `new-${++newRowSeq.current}`,
        userId: "",
        name: "",
        required: false,
        lobIds: [],
      },
    ]);

  return (
    <div className="mt-4">
      <div className="adminFormSectionTitle">Approvers</div>

      {kind === "sales" && (
        <p className="text-muted">
          In addition to the Sales Executive, who should be able to approve the
          project?
        </p>
      )}

      <div
        className={`approvalLevelApprovers${
          showLobFilter ? "" : " approvalLevelApprovers--noLob"
        }`}
      >
        <div className="approvalLevelApprovers__header">APPROVERS</div>
        {approvers.length === 0 ? (
          <div className="approvalLevelApprovers__empty">
            No approvers assigned yet.
          </div>
        ) : (
          <div className="approvalLevelApprovers__rows">
            <div className="approvalLevelApprovers__rowHeader">
              <span>Approver</span>
              {showLobFilter && <span>Lines of Business</span>}
              <span className="approvalLevelApprovers__requiredCell">
                Required?
              </span>
              <span className="approvalLevelApprovers__actions" />
            </div>
            {approvers.map((row, idx) => {
              const isNew = !row.id || row.id.startsWith("new-");
              const remainingUsers = availableUsers.filter(
                (u) => !usedUserIds.has(u.id) || u.id === row.userId
              );
              const userOptions = remainingUsers.map((u) => ({
                value: u.id,
                label: u.name,
              }));
              return (
                <div
                  key={row.id ?? idx}
                  className="approvalLevelApprovers__row"
                >
                  {isNew ? (
                    <RequiredReactSelect
                      label={null as any}
                      value={
                        row.userId
                          ? userOptions.find((o) => o.value === row.userId)
                          : undefined
                      }
                      onChange={(opt: any) => {
                        const userId = opt ? String(opt.value) : "";
                        const match = availableUsers.find(
                          (u) => u.id === userId
                        );
                        patchAt(idx, {
                          userId,
                          name: match?.name ?? "",
                        });
                      }}
                      options={userOptions}
                      placeholder="Select an approver"
                    />
                  ) : (
                    <span>{row.name}</span>
                  )}
                  {showLobFilter && (
                    <RequiredReactSelect
                      label={null as any}
                      isMulti
                      value={
                        row.lobIds
                          .map((id) => lobOptions.find((o) => o.value === id))
                          .filter(Boolean) as any
                      }
                      onChange={(opt: any) =>
                        patchAt(idx, {
                          lobIds: Array.isArray(opt)
                            ? opt.map((o) => String(o.value))
                            : [],
                        })
                      }
                      options={lobOptions}
                      placeholder="Any Line of Business"
                    />
                  )}
                  <span className="approvalLevelApprovers__requiredCell">
                    <input
                      type="checkbox"
                      className="approvalLevelApprovers__requiredCheck"
                      aria-label={`Required: ${row.name || "new approver"}`}
                      checked={row.required}
                      onChange={(e) =>
                        patchAt(idx, { required: e.target.checked })
                      }
                    />
                  </span>
                  <span className="approvalLevelApprovers__actions">
                    <button
                      type="button"
                      className="approvalLevelApprovers__actionBtn"
                      aria-label={`Remove approver ${
                        row.name || "(unsaved row)"
                      }`}
                      onClick={() => removeAt(idx)}
                    >
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        className="approvalLevelApprovers__actionIcon approvalLevelApprovers__actionIcon--remove"
                      />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Button
        variant="link"
        type="button"
        className="approvalLevelApprovers__addLink"
        onClick={addRow}
      >
        Add an Approver +
      </Button>
    </div>
  );
};
